/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import isNil from 'lodash.isnil';
import useElementResize from '@ion/core/src/hooks/useElementResize';
import {translate} from '@ion/core/src/locale/locale';
import {AssociatedAssetType, BaseAsset} from '@ion/core/src/types/entities';
import {AssetNameStatusHeader} from '@ion/dls/src/components/assetdetails/current/AssetNameStatusHeader';
import {isCurrentTheme, useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {
  fetchEditableDevice,
  getAssociatedAssetsForAsset,
  getSelectedAssetTools,
  setAssetDetailView,
  setSelectedAsset,
  setToolPanel
} from '../../../store/actions/tracking';
import {
  assetDetailViewSelector,
  getListAssetsSelector,
  searchSelectedSelector,
  selectedAssetSelector,
  showPopupSelector,
  toolPanelOpenSelector
} from '../../../store/selectors/tracking-selectors';
import AssetCard from '../AssetCard';
import AssetDetailOverviewPanel from '../AssetDetailOverviewPanel';
import ContainedAsset from '../ContainedAsset';
import {getAssetSummaryCardProps} from '../getAssetSummaryCardProps';
import useToggleAssignedAssetsPanel from '../hooks/sidesheet/useToggleAssignedAssetsPanel';

const useStyles = makeStyles(() => ({
  detailTombstone : {
    height  : '100vh',
    padding : '10px'
  }
}));
const SIDESHEET_DETAIL_TAB_ID = 'sideSheetTabContainer';
/**
 * @SidesheetDetailTabs
 * Returns container component for Asset Card, details and history pane;l
 *
 *  ```typescript
 *   import SidesheetDetailTabs from '@/routes/tracking/sidesheet/SidesheetDetailTabs';
 *
 *   <SidesheetDetailTabs />
 *  ```
 */
const SidesheetDetailTabs = ({trackingV2 = false}: {trackingV2?: boolean}) => {
  const classes                       = useStyles();
  const {mode}                        = useThemeMode();
  const dispatch                      = useDispatch();
  const [divHeight, setDivHeight]     = useState(0);
  const {data: listAssets}            = useSelector(getListAssetsSelector);
  const showPopup                     = useSelector(showPopupSelector);
  const selectedAsset                 = useSelector(selectedAssetSelector);
  const toolPanelOpen                 = useSelector(toolPanelOpenSelector);
  const searchSelected                = useSelector(searchSelectedSelector);
  const assetDetailView               = useSelector(assetDetailViewSelector);
  const dimentions                    = useElementResize(SIDESHEET_DETAIL_TAB_ID);
  const {
    onToggleAssignedAssetsPanel,
    assignedAssetsPanelsOpen
  } = useToggleAssignedAssetsPanel();

  const assetLabels = useMemo(() => ({...translate('tracking:list.detailed', {returnObjects: true})}), []);
  // only computed for the current theme - the legacy AssetCard below is
  // left entirely as-is otherwise, matching AssetCard.tsx's own
  // isCurrentTheme()-gated summaryCardProps computation ...
  const summaryCardProps = useMemo(() => (
    isCurrentTheme() && selectedAsset ? getAssetSummaryCardProps(selectedAsset) : null
  ), [selectedAsset, mode]);

  useEffect(() => {
    if (dimentions.height) {
      setDivHeight(dimentions.height);
    }
  }, [dimentions?.height]);

  // [ION-APP TODO]: onContainsClick is an exact duplicate of onContainsClick in
  // SidesheetDetailTabs.tsx and needs to be refactored and shared between the two components.
  const onContainsClick = useCallback((asset: BaseAsset) => {
    const temp = [...toolPanelOpen];
    const clickedPanel = temp.find(clicked => parseFloat(clicked.assetId) === asset.id);
    const matchingAssetIndex = temp.findIndex(item => item === clickedPanel);
    let updatedTemp;
    if (!showPopup) {
      if (!clickedPanel) {
        if (matchingAssetIndex < 0) {
          updatedTemp = [...temp, {assetId: asset.id, open: true}];
        }
        dispatch(setToolPanel(updatedTemp));
        dispatch(getSelectedAssetTools(asset, true) as any);
      } else {
        if (matchingAssetIndex > -1) {
          temp[matchingAssetIndex] = {assetId: asset.id, open: !clickedPanel.open};
          updatedTemp = temp;
          const currentAssetAccordionOpen = temp.find(({assetId, open}) => Number(assetId) === asset?.id && open);
          if (!isNil(currentAssetAccordionOpen)) {
            dispatch(getSelectedAssetTools(asset, true) as any);
          }
        }
        dispatch(setToolPanel(updatedTemp));
      }
    } else {
      if (matchingAssetIndex > -1) {
        temp[matchingAssetIndex] = {assetId: asset.id, open: false};
        updatedTemp = temp;
        dispatch(setToolPanel(updatedTemp));
      }
    }
  }, [toolPanelOpen, showPopup]);

  const onDeviceEdit = useCallback(function() {
    dispatch(fetchEditableDevice(selectedAsset) as any);
  }, [selectedAsset]);

  const onSelectAssociatedAsset = useCallback(function(e: MouseEvent, selectedAssociatedAsset: AssociatedAssetType) {
    e.stopPropagation();
    const id = selectedAssociatedAsset?.id;
    const cachedAsset = listAssets.find(asset => asset.getAssetValue('assetId') === id);
    if (cachedAsset) {
      dispatch(setSelectedAsset({asset: cachedAsset}) as any);
      dispatch(setAssetDetailView({value: true}) as any);
    } else {
      const originatingAssetId = selectedAssociatedAsset?.originatingAssetId;
      // Go fetch the associated asset details
      dispatch(getAssociatedAssetsForAsset({
        assetId           : originatingAssetId ?? id,
        includeVisibility : true,
        includeIONTags    : false,
        associatedAssetId : id,
        trackingV2
      }) as any);
    }
  }, [listAssets]);

  if (selectedAsset) {
    return (
      <>
        <div id={SIDESHEET_DETAIL_TAB_ID}>
          {summaryCardProps ? (
            <AssetNameStatusHeader assetType={summaryCardProps.assetType}
              assetName={summaryCardProps.assetName}
              alias={summaryCardProps.alias}
              status={summaryCardProps.status} />
          ) : (
            <AssetCard asset={selectedAsset}
              labels={assetLabels}
              assetDetailView={assetDetailView}
              onContainsClick={onContainsClick}
              onToggleAssignedAssetsPanel={onToggleAssignedAssetsPanel}
              assignedAssetsPanelsOpen={assignedAssetsPanelsOpen}
              toolPanelOpen={toolPanelOpen}
              searchSelected={searchSelected}
              onDeviceEdit={onDeviceEdit} />
          )}
        </div>
        <AssetDetailOverviewPanel asset={selectedAsset}
          labels={assetLabels}
          assetDetailView={assetDetailView}
          onContainsClick={onContainsClick}
          toolPanelOpen={toolPanelOpen}
          onToggleAssignedAssetsPanel={onToggleAssignedAssetsPanel}
          assignedAssetsPanelsOpen={assignedAssetsPanelsOpen}
          ContainedComponent={ContainedAsset}
          onSelectAssociatedAsset={onSelectAssociatedAsset}
          parentHeight={divHeight}
          trackingV2={trackingV2} />
      </>
    );
  }
  return null;
};
export default React.memo(SidesheetDetailTabs);
